import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
  Snackbar,
  Alert,
  Stack,
} from '@mui/material';
import { useLocation } from 'react-router-dom';

import { FormFieldError, FormFieldType } from '../types';
import { WEBHOOK_URL } from '../configs';
import SampleScreenshot from '../components/SampleScreenshot';
import { maximumFilesAllowed, PlatformType, postFormats, supportedTypes } from '../common/constants';
import UploadPhotoForm from '../components/UploadPhotoForm';
import { LabelWithAsterisk } from '../components/LabelWithAsterisk';

const textFieldStyle = {
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    fontSize: '0.875rem',
    marginTop: '0px',
  },
  '& .MuiOutlinedInput-input': {
    padding: '9px 14px',
  },
};

const emptyFormFieldError: FormFieldError = {
  socialMediaHandle: '',
  fullName: '',
  campaignName: '',
  platform: '',
  postFormat: '',
};

const SelfReporting: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const campaignNameParam = queryParams.get('campaignName');
  const fullNameParam = queryParams.get('fullName');
  const teamNameParam = queryParams.get('teamName');

  const emptyFormField: FormFieldType = {
    socialMediaHandle: '',
    fullName: fullNameParam || '',
    campaignName: campaignNameParam || '',
    postFormat: '',
  };

  const [formField, setFormField] = useState<FormFieldType>(emptyFormField);
  const [formFieldError, setFormFieldError] = useState<FormFieldError>(emptyFormFieldError);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'socialMediaHandle') {
      setFormField({ ...formField, ...{ socialMediaHandle: value.startsWith('@') ? value : '@' + value.replace(/^@/, '') } });
    } else if (name === 'campaignName') {
      setFormField({ ...formField, ...{ campaignName: value } });
    } else if (name === 'fullName') {
      setFormField({ ...formField, ...{ fullName: value } });
    } else if (name === 'contentLink') {
      setFormField({ ...formField, ...{ contentLink: value } });
    }
  };

  const isUploadMultiplePhotos = useMemo(
    () =>
      (formField.platform === PlatformType.Lemon8 && formField.postFormat) ||
      (formField.platform === PlatformType.TikTok &&
        (formField.postFormat === 'Video' || formField.postFormat === 'Multipost')) ||
      (formField.platform === PlatformType.Facebook && formField.postFormat === 'Video') ||
      formField.platform === PlatformType.Instagram ||
      formField.platform === PlatformType.XHS,
    [formField],
  );

  const isStoryPostType = useMemo(() => formField.postFormat === 'Story', [formField]);

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handlePlatformChange = (event: SelectChangeEvent<string>) => {
    setFormField((previousValue) => ({ ...previousValue, ...{ platform: event.target.value as PlatformType, postFormat: '' } }));
  };

  useEffect(() => {
    setFormField((previousValue) => ({ ...previousValue, ...{ type: 'Reach' } }));
  }, [formField.platform]);

  const handlePostFormatChange = (event: SelectChangeEvent<string>) => {
    setFormField((previousValue) => ({ ...previousValue, ...{ postFormat: event.target.value, type: 'Reach' } }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    if (event.target.files) {
      let errorMessage = '';

      if (!formField?.files?.[type]) {
        formField.files ??= {};
        formField.files[type] = [];
      }

      if (formField.files[type].length === maximumFilesAllowed) {
        errorMessage = 'You can upload up to 10 files only.';
      }

      const invalidFiles = formField.files[type].filter((file) => !supportedTypes.includes(file.type));
      if (invalidFiles.length > 0) {
        errorMessage = 'Only image files (jpg, png, gif, webp) are supported.';
      }

      const oversizedFiles = formField.files[type].filter((file: File) => file.size > 100 * 1024 * 1024); // 100 MB in bytes
      if (oversizedFiles.length) {
        errorMessage = 'File size exceeds 100 MB.';
      }

      if (errorMessage) {
        setFormFieldError((previousValue) => ({
          ...previousValue,
          files: {
            ...(previousValue?.files ?? {}),
            [type]: errorMessage,
          },
        }));
        return;
      }

      const newFiles = Array.from(event.target.files);
      setFormField((previousValue) => {
        if (previousValue?.files?.[type]) {
          return {
            ...previousValue,
            files: {
              ...previousValue.files,
              [type]: [...previousValue.files[type], ...newFiles],
            },
          };
        }
        return previousValue;
      });

      setFormFieldError((previousValue) => ({
        ...previousValue,
        files: {
          ...previousValue?.files,
          [type]: '',
        },
      }));
    }
  };

  const handleRemovePhoto = (index: number, type: string) => {
    if (!formField?.files?.[type]) {
      return;
    }
    const newFormField: FormFieldType = { ...formField };
    if (!newFormField?.files?.[type]) {
      return;
    }

    newFormField.files[type] = formField.files[type].filter((file: File, i: number) => i !== index);

    setFormField(newFormField);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setFormFieldError(emptyFormFieldError);

    let valid = true;

    if (!formField.fullName) {
      setFormFieldError((previousValue) => ({ ...previousValue, ...{ fullName: 'Full Name is required' } }));
      valid = false;
    }
    if (!isStoryPostType && formField.postFormat && !formField.contentLink) {
      setFormFieldError((previousValue) => ({ ...previousValue, ...{ contentLink: 'Content Link is required' } }));
      valid = false;
    }
    if (!formField.socialMediaHandle) {
      setFormFieldError((previousValue) => ({ ...previousValue, ...{ socialMediaHandle: 'KOL Handle is required' } }));
      valid = false;
    }
    if (!formField.platform) {
      setFormFieldError((previousValue) => ({ ...previousValue, ...{ platform: 'Platform is required' } }));
      valid = false;
    }
    if (!formField.postFormat) {
      setFormFieldError((previousValue) => ({ ...previousValue, ...{ postFormat: 'Post Format is required' } }));
      valid = false;
    }

    if (!formField?.files) {
      if (isUploadMultiplePhotos) {
        setFormFieldError((previousValue) => ({
          ...previousValue,
          files: {
            ...previousValue?.files,
            engagePhoto: 'At least one engagement photo is required',
            reachPhoto: 'At least one reach photo is required',
          },
        }));
      } else {
        setFormFieldError((previousValue) => ({
          ...previousValue,
          files: {
            ...previousValue?.files,
            singlePhoto: 'At least one photo is required',
          },
        }));
      }
      valid = false;
    }
    if (!valid) {
      return;
    }

    setError(null);
    setLoading(true);

    const formData = new FormData();
    formData.append('socialMediaHandle', formField.socialMediaHandle);
    formData.append('fullName', formField.fullName);
    formData.append('postFormat', formField.postFormat);

    if (formField.contentLink) {
      formData.append('contentLink', formField.contentLink);
    }

    if (teamNameParam) {
      formData.append('teamName', teamNameParam);
    }
    if (formField.platform) {
      formData.append('platform', formField.platform);
    }

    formData.append('campaignName', formField.campaignName);

    if (formField?.files && Object.keys(formField?.files)?.length) {
      for (const key in formField.files) {
        for (let i = 0; i < formField.files[key].length; i++) {
          formData.append(key, formField.files[key][i]);
        }
      }
    }

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        setError('Something went wrong!');
      }
      setOpenSnackbar(true);
      setError(null);
      setFormField(emptyFormField);
      try {
        const result = await response.json();
        console.log('result: ', result);
      } catch (error: any) {
        console.log('Parse response error: ', error?.message);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ padding: 4, marginTop: 1 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            textAlign: 'center',
            fontSize: {
              xs: '1.2rem',
              md: '1.5rem',
            },
          }}
        >
          Upload Your Campaign Analytics
        </Typography>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack sx={{ direction: 'row' }}>
              <Typography variant="body1">Campaign Name</Typography>
              <TextField
                variant="outlined"
                fullWidth
                value={formField.campaignName}
                onChange={handleInputChange}
                disabled={!!campaignNameParam}
                name="campaignName"
                sx={{
                  ...textFieldStyle,
                }}
              />
            </Stack>

            <Stack
              sx={{
                flexDirection: {
                  sx: 'column',
                  md: 'row',
                },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: {
                    md: '50%',
                  },
                }}
              >
                <LabelWithAsterisk text="Full Name" />
                <TextField
                  variant="outlined"
                  value={formField.fullName}
                  fullWidth
                  onChange={handleInputChange}
                  name="fullName"
                  placeholder="Enter your full name"
                  error={!!formFieldError.fullName}
                  helperText={formFieldError.fullName}
                  sx={{
                    ...textFieldStyle,
                  }}
                />
              </Box>

              <Box
                sx={{
                  width: {
                    md: '50%',
                  },
                }}
              >
                <LabelWithAsterisk text="Social Media Handle" />
                <TextField
                  variant="outlined"
                  value={formField.socialMediaHandle}
                  onChange={handleInputChange}
                  fullWidth
                  name="socialMediaHandle"
                  placeholder="@yourusername"
                  error={!!formFieldError.socialMediaHandle}
                  helperText={formFieldError.socialMediaHandle}
                  sx={{
                    ...textFieldStyle,
                  }}
                />
              </Box>
            </Stack>

            <Stack
              sx={{
                flexDirection: {
                  sx: 'column',
                  md: 'row',
                },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: {
                    md: '50%',
                  },
                }}
              >
                <LabelWithAsterisk text="Platform" />
                <FormControl fullWidth variant="outlined" sx={{ ...textFieldStyle }} error={!!formFieldError.platform}>
                  <Select value={formField.platform || ''} onChange={handlePlatformChange}>
                    <MenuItem value="">
                      <em>Select Platform</em>
                    </MenuItem>
                    {Object.values(PlatformType).map((item, index) => (
                      <MenuItem key={index} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                  {formFieldError.platform && (
                    <Typography variant="body2" color="error">
                      {formFieldError.platform}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box
                sx={{
                  width: {
                    md: '50%',
                  },
                }}
              >
                <LabelWithAsterisk text="Post Format" />
                <FormControl fullWidth variant="outlined" sx={{ ...textFieldStyle }} error={!!formFieldError.postFormat}>
                  <Select value={formField.postFormat} onChange={handlePostFormatChange}>
                    <MenuItem value="">
                      <em>Post Format</em>
                    </MenuItem>
                    {formField.platform &&
                      postFormats[formField.platform].map((item, index) => (
                        <MenuItem key={index} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                  </Select>
                  {formFieldError.postFormat && (
                    <Typography variant="body2" color="error">
                      {formFieldError.postFormat}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Stack>

            {formField.postFormat && !isStoryPostType && (
              <Box
                sx={{
                  width: {
                    md: '50%',
                  },
                }}
              >
                <LabelWithAsterisk text="Content Link" />
                <TextField
                  variant="outlined"
                  value={formField.contentLink}
                  fullWidth
                  onChange={handleInputChange}
                  name="contentLink"
                  error={!!formFieldError.contentLink}
                  helperText={formFieldError.contentLink}
                  sx={{
                    ...textFieldStyle,
                  }}
                />
              </Box>
            )}

            {formField.postFormat && !isUploadMultiplePhotos && (
              <>
                <Stack flexDirection="column">
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: {
                        xs: '1rem',
                        md: '1.25rem',
                      },
                    }}
                  >
                    Platform Analytics Screenshot *
                  </Typography>
                  {formField.platform && formField.postFormat && (
                    <SampleScreenshot
                      key={`${formField.platform}-${formField.postFormat}`}
                      platform={formField.platform}
                      postFormat={formField.postFormat}
                      type=""
                    />
                  )}
                </Stack>
                <UploadPhotoForm
                  handleFileChange={(event: React.ChangeEvent<HTMLInputElement>) => handleFileChange(event, 'singlePhoto')}
                  handleRemovePhoto={(index: number) => handleRemovePhoto(index, 'singlePhoto')}
                  files={formField?.files?.['singlePhoto']}
                  filesError={formFieldError?.files?.['singlePhoto']}
                />
              </>
            )}

            {formField.postFormat && isUploadMultiplePhotos && (
              <>
                <Stack flexDirection="column">
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: {
                        xs: '1rem',
                        md: '1.25rem',
                      },
                    }}
                  >
                    Platform Reach Analytics Screenshot *
                  </Typography>
                  {formField.platform && formField.postFormat && (
                    <SampleScreenshot
                      key={`${formField.platform}-Reach-${formField.postFormat}`}
                      platform={formField.platform}
                      postFormat={formField.postFormat}
                      type="Reach"
                    />
                  )}
                </Stack>

                <UploadPhotoForm
                  handleFileChange={(event: React.ChangeEvent<HTMLInputElement>) => handleFileChange(event, 'reachPhoto')}
                  handleRemovePhoto={(index: number) => handleRemovePhoto(index, 'reachPhoto')}
                  files={formField?.files?.['reachPhoto']}
                  filesError={formFieldError?.files?.['reachPhoto']}
                />
              </>
            )}

            {formField.postFormat && isUploadMultiplePhotos && (
              <>
                <Stack flexDirection="column">
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: {
                        xs: '1rem',
                        md: '1.25rem',
                      },
                    }}
                  >
                    Platform Engagement Analytics Screenshot *
                  </Typography>

                  {formField.platform && formField.postFormat && (
                    <SampleScreenshot
                      key={`${formField.platform}-Engagement-${formField.postFormat}`}
                      platform={formField.platform}
                      postFormat={formField.postFormat}
                      type="Engagement"
                    />
                  )}
                </Stack>

                <UploadPhotoForm
                  handleFileChange={(event: React.ChangeEvent<HTMLInputElement>) => handleFileChange(event, 'engagePhoto')}
                  handleRemovePhoto={(index: number) => handleRemovePhoto(index, 'engagePhoto')}
                  files={formField?.files?.['engagePhoto']}
                  filesError={formFieldError?.files?.['engagePhoto']}
                />
              </>
            )}

            <Button
              variant="contained"
              disabled={loading}
              color="primary"
              type="submit"
              sx={{
                marginTop: 2,
                textTransform: 'none',
                fontWeight: 450,
                background: 'linear-gradient(90deg, #C94BD2 0%, #4AA9EC 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #B945C4 0%, #3DA0E0 100%)',
                },
              }}
            >
              {loading ? 'Submitting...' : 'Submit Analytics'}
            </Button>
            <Snackbar
              open={openSnackbar}
              autoHideDuration={3000}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              onClose={handleCloseSnackbar}
            >
              <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                Form submitted successfully!
              </Alert>
            </Snackbar>
          </Box>
        </form>
        {error && <div style={{ color: 'red' }}>{error}</div>}
      </Paper>
    </Container>
  );
};

export default SelfReporting;
